import React from 'react';
import './PortfolioTemplates.css';

export const TemplateRenderer = ({ template, profile }) => {
  switch (template) {
    case 'dark':      return <DarkProTemplate profile={profile} />;
    case 'creative':  return <CreativeTemplate profile={profile} />;
    case 'executive': return <ExecutiveTemplate profile={profile} />;
    case 'neon':      return <NeonTemplate profile={profile} />;
    case 'elegant':   return <ElegantTemplate profile={profile} />;
    default:          return <MinimalTemplate profile={profile} />;
  }
};

// ── helpers ───────────────────────────────────────────────────────────────────
const parse = (p = {}) => ({
  ...p,
  skills:     p.skills?.split(',').map(s => s.trim()).filter(Boolean) || [],
  experience: p.workExperience?.filter(e => e.company)   || [],
  education:  p.education?.filter(e => e.institution)    || [],
  projects:   p.projects?.filter(pr => pr.name)          || [],
});

const API = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

// Split description into bullet points on ". " before a capital letter
const toBullets = (text = '') => {
  if (!text) return [];
  const points = text
    .split(/\.\s+(?=[A-Z])/)
    .map(s => s.replace(/\.$/, '').trim())
    .filter(Boolean);
  return points.length > 1 ? points : [];
};

const Bullets = ({ text, className }) => {
  const bullets = toBullets(text);
  if (bullets.length > 1) {
    return (
      <ul className={`tpl-bullets ${className || ''}`}>
        {bullets.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    );
  }
  return <div className={className}>{text}</div>;
};

const Avatar = ({ url, name, size = 80, bg = '#e0e0e0', color = '#555', fontSize = '1.6rem', radius = '50%' }) => (
  url ? (
    <img
      src={`${API}${url}`}
      alt={name || 'avatar'}
      style={{ width: size, height: size, borderRadius: radius, objectFit: 'cover', display: 'block', flexShrink: 0 }}
    />
  ) : (
    <div style={{ width: size, height: size, borderRadius: radius, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize, fontWeight: 700, color, flexShrink: 0 }}>
      {name?.[0]?.toUpperCase() || 'U'}
    </div>
  )
);

// ─────────────────────────────────────────────────────────────────────────────
// MINIMAL
// ─────────────────────────────────────────────────────────────────────────────
export const MinimalTemplate = ({ profile }) => {
  const d = parse(profile);
  return (
    <div className="min-pf pf-tpl-root">
      <div className="min-header">
        <div className="min-header-inner">
        <Avatar url={d.avatarUrl} name={d.fullName} size={80} bg="#efefef" color="#666" fontSize="1.6rem" />
        <div className="min-header-text">
          <div className="min-name">{d.fullName || 'Your Name'}</div>
          <div className="min-title">{d.jobTitle || 'Professional'}</div>
          <div className="min-contacts">
            {d.email    && <span className="min-contact-item">{d.email}</span>}
            {d.phone    && <span className="min-contact-item">{d.phone}</span>}
            {d.location && <span className="min-contact-item">{d.location}</span>}
            {d.linkedIn && <span className="min-contact-item"><a href={d.linkedIn} target="_blank" rel="noreferrer">LinkedIn ↗</a></span>}
            {d.github   && <span className="min-contact-item"><a href={d.github}   target="_blank" rel="noreferrer">GitHub ↗</a></span>}
            {d.portfolio && <span className="min-contact-item"><a href={d.portfolio} target="_blank" rel="noreferrer">Website ↗</a></span>}
          </div>
        </div>
        </div>
      </div>

      <div className="min-body">
        {d.professionalSummary && (
          <section className="min-section">
            <div className="min-section-label">About</div>
            <p className="min-summary">{d.professionalSummary}</p>
          </section>
        )}

        {d.experience.length > 0 && (
          <section className="min-section">
            <div className="min-section-label">Experience</div>
            {d.experience.map((exp, i) => (
              <div key={i} className="min-exp-item">
                <div className="min-exp-date">{exp.duration}</div>
                <div>
                  <div className="min-exp-role">{exp.position}</div>
                  <div className="min-exp-company">{exp.company}</div>
                  {exp.description && <Bullets text={exp.description} className="min-exp-desc" />}
                </div>
              </div>
            ))}
          </section>
        )}

        {d.education.length > 0 && (
          <section className="min-section">
            <div className="min-section-label">Education</div>
            {d.education.map((edu, i) => (
              <div key={i} className="min-exp-item">
                <div className="min-exp-date">{edu.year}</div>
                <div>
                  <div className="min-exp-role">{edu.degree}{edu.field ? ` in ${edu.field}` : ''}</div>
                  <div className="min-exp-company">{edu.institution}</div>
                </div>
              </div>
            ))}
          </section>
        )}

        {d.skills.length > 0 && (
          <section className="min-section">
            <div className="min-section-label">Skills</div>
            <div>{d.skills.map((sk, i) => <span key={i} className="min-skill">{sk}</span>)}</div>
          </section>
        )}

        {d.projects.length > 0 && (
          <section className="min-section">
            <div className="min-section-label">Projects</div>
            {d.projects.map((pr, i) => (
              <div key={i} className="min-proj-item">
                <div className="min-proj-name">{pr.name}</div>
                {pr.technologies && <div className="min-proj-tech">{pr.technologies}</div>}
                {pr.description  && <div className="min-proj-desc">{pr.description}</div>}
              </div>
            ))}
          </section>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DARK PRO
// ─────────────────────────────────────────────────────────────────────────────
export const DarkProTemplate = ({ profile }) => {
  const d = parse(profile);
  return (
    <div className="dp-pf pf-tpl-root">
      <div className="dp-hero">
        <div className="dp-hero-inner">
        <Avatar url={d.avatarUrl} name={d.fullName} size={88} bg="rgba(167,139,250,0.15)" color="#a78bfa" fontSize="2rem" />
        <div className="dp-hero-text">
          <div className="dp-name">{d.fullName || 'Your Name'}</div>
          <div className="dp-title">{d.jobTitle || 'Professional'}</div>
          <div className="dp-contacts">
            {d.email    && <span className="dp-contact">{d.email}</span>}
            {d.phone    && <span className="dp-contact">{d.phone}</span>}
            {d.location && <span className="dp-contact">{d.location}</span>}
            {d.linkedIn && <span className="dp-contact"><a href={d.linkedIn} target="_blank" rel="noreferrer">LinkedIn ↗</a></span>}
            {d.github   && <span className="dp-contact"><a href={d.github}   target="_blank" rel="noreferrer">GitHub ↗</a></span>}
          </div>
        </div>
        </div>
      </div>

      <div className="dp-body">
        {d.professionalSummary && (
          <section className="dp-section">
            <div className="dp-label">About</div>
            <p className="dp-summary">{d.professionalSummary}</p>
          </section>
        )}

        {d.experience.length > 0 && (
          <section className="dp-section">
            <div className="dp-label">Experience</div>
            {d.experience.map((exp, i) => (
              <div key={i} className="dp-card">
                <div className="dp-card-header">
                  <div>
                    <div className="dp-card-role">{exp.position}</div>
                    <div className="dp-card-company">{exp.company}</div>
                  </div>
                  <div className="dp-card-date">{exp.duration}</div>
                </div>
                {exp.description && <Bullets text={exp.description} className="dp-card-desc" />}
              </div>
            ))}
          </section>
        )}

        {d.education.length > 0 && (
          <section className="dp-section">
            <div className="dp-label">Education</div>
            {d.education.map((edu, i) => (
              <div key={i} className="dp-card">
                <div className="dp-card-header">
                  <div>
                    <div className="dp-card-role">{edu.degree}{edu.field ? ` in ${edu.field}` : ''}</div>
                    <div className="dp-card-company">{edu.institution}</div>
                  </div>
                  <div className="dp-card-date">{edu.year}</div>
                </div>
              </div>
            ))}
          </section>
        )}

        {d.skills.length > 0 && (
          <section className="dp-section">
            <div className="dp-label">Skills</div>
            <div>{d.skills.map((sk, i) => <span key={i} className="dp-skill">{sk}</span>)}</div>
          </section>
        )}

        {d.projects.length > 0 && (
          <section className="dp-section">
            <div className="dp-label">Projects</div>
            <div className="dp-proj-grid">
              {d.projects.map((pr, i) => (
                <div key={i} className="dp-proj-card">
                  <div className="dp-proj-name">{pr.name}</div>
                  {pr.technologies && <div className="dp-proj-tech">{pr.technologies}</div>}
                  {pr.description  && <div className="dp-proj-desc">{pr.description}</div>}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CREATIVE
// ─────────────────────────────────────────────────────────────────────────────
export const CreativeTemplate = ({ profile }) => {
  const d = parse(profile);
  return (
    <div className="cr-pf pf-tpl-root">
      {/* ── Sidebar ── */}
      <aside className="cr-sidebar">
        <div className="cr-avatar-wrap">
          <Avatar url={d.avatarUrl} name={d.fullName} size={110} bg="rgba(255,255,255,0.15)" color="#fff" fontSize="2.4rem" />
          <div>
            <div className="cr-name">{d.fullName || 'Your Name'}</div>
            <div className="cr-title">{d.jobTitle || 'Professional'}</div>
          </div>
        </div>

        {(d.email || d.phone || d.location || d.linkedIn || d.github) && (
          <div>
            <div className="cr-sidebar-label">Contact</div>
            {d.email    && <div className="cr-contact-row">✉ {d.email}</div>}
            {d.phone    && <div className="cr-contact-row">📞 {d.phone}</div>}
            {d.location && <div className="cr-contact-row">📍 {d.location}</div>}
            {d.linkedIn && <div className="cr-contact-row"><a href={d.linkedIn} target="_blank" rel="noreferrer">🔗 LinkedIn</a></div>}
            {d.github   && <div className="cr-contact-row"><a href={d.github}   target="_blank" rel="noreferrer">💻 GitHub</a></div>}
          </div>
        )}

        {d.skills.length > 0 && (
          <div>
            <div className="cr-sidebar-label">Skills</div>
            <div>{d.skills.map((sk, i) => <span key={i} className="cr-skill">{sk}</span>)}</div>
          </div>
        )}

        {d.education.length > 0 && (
          <div>
            <div className="cr-sidebar-label">Education</div>
            {d.education.map((edu, i) => (
              <div key={i} className="cr-edu-item">
                <div className="cr-edu-deg">{edu.degree}{edu.field ? ` in ${edu.field}` : ''}</div>
                <div className="cr-edu-inst">{edu.institution}</div>
                <div className="cr-edu-year">{edu.year}</div>
              </div>
            ))}
          </div>
        )}
      </aside>

      {/* ── Main content ── */}
      <div className="cr-content">
        {d.professionalSummary && (
          <section className="cr-section">
            <div className="cr-section-label">About</div>
            <p className="cr-summary">{d.professionalSummary}</p>
          </section>
        )}

        {d.experience.length > 0 && (
          <section className="cr-section">
            <div className="cr-section-label">Experience</div>
            {d.experience.map((exp, i) => (
              <div key={i} className="cr-exp-item">
                <div className="cr-exp-header">
                  <div className="cr-exp-role">{exp.position}</div>
                  <div className="cr-exp-date">{exp.duration}</div>
                </div>
                <div className="cr-exp-company">{exp.company}</div>
                {exp.description && <Bullets text={exp.description} className="cr-exp-desc" />}
              </div>
            ))}
          </section>
        )}

        {d.projects.length > 0 && (
          <section className="cr-section">
            <div className="cr-section-label">Projects</div>
            <div className="cr-proj-grid">
              {d.projects.map((pr, i) => (
                <div key={i} className="cr-proj-card">
                  <div className="cr-proj-name">{pr.name}</div>
                  {pr.technologies && <div className="cr-proj-tech">{pr.technologies}</div>}
                  {pr.description  && <div className="cr-proj-desc">{pr.description}</div>}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// EXECUTIVE
// ─────────────────────────────────────────────────────────────────────────────
export const ExecutiveTemplate = ({ profile }) => {
  const d = parse(profile);
  return (
    <div className="ex-pf pf-tpl-root">
      <div className="ex-banner">
        <div className="ex-banner-inner">
          <div className="ex-banner-text">
            <div className="ex-banner-name">{d.fullName || 'Your Name'}</div>
            <div className="ex-banner-title">{d.jobTitle || 'Professional'}</div>
          </div>
        </div>
      </div>

      <div className="ex-profile-row">
        <div className="ex-avatar-wrap">
          <Avatar url={d.avatarUrl} name={d.fullName} size={104} bg="#1e3a5f" color="#fff" fontSize="2rem" radius="8px" />
        </div>
        <div className="ex-meta">
          <div className="ex-contacts">
            {d.email    && <span className="ex-contact">{d.email}</span>}
            {d.phone    && <span className="ex-contact">{d.phone}</span>}
            {d.location && <span className="ex-contact">{d.location}</span>}
            {d.linkedIn && <span className="ex-contact"><a href={d.linkedIn} target="_blank" rel="noreferrer">LinkedIn ↗</a></span>}
            {d.github   && <span className="ex-contact"><a href={d.github}   target="_blank" rel="noreferrer">GitHub ↗</a></span>}
            {d.portfolio && <span className="ex-contact"><a href={d.portfolio} target="_blank" rel="noreferrer">Website ↗</a></span>}
          </div>
        </div>
      </div>

      <div className="ex-main">
        <aside className="ex-sidebar">
          {d.skills.length > 0 && (
            <>
              <div className="ex-section-label">Skills</div>
              {d.skills.map((sk, i) => <div key={i} className="ex-skill-item">{sk}</div>)}
            </>
          )}
          {d.education.length > 0 && (
            <>
              <div className="ex-section-label">Education</div>
              {d.education.map((edu, i) => (
                <div key={i} className="ex-edu-item">
                  <div className="ex-edu-deg">{edu.degree}{edu.field ? ` in ${edu.field}` : ''}</div>
                  <div className="ex-edu-inst">{edu.institution}</div>
                  <div className="ex-edu-year">{edu.year}</div>
                </div>
              ))}
            </>
          )}
          {d.certifications && (
            <>
              <div className="ex-section-label">Certifications</div>
              <div style={{ fontSize: '0.79rem', color: '#444', lineHeight: 1.7 }}>{d.certifications}</div>
            </>
          )}
        </aside>

        <div className="ex-content">
          {d.professionalSummary && (
            <section style={{ marginBottom: 28 }}>
              <div className="ex-section-label" style={{ marginTop: 0 }}>Summary</div>
              <p className="ex-summary">{d.professionalSummary}</p>
            </section>
          )}

          {d.experience.length > 0 && (
            <section style={{ marginBottom: 28 }}>
              <div className="ex-section-label">Experience</div>
              {d.experience.map((exp, i) => (
                <div key={i} className="ex-exp-item">
                  <div className="ex-exp-header">
                    <div className="ex-exp-role">{exp.position}</div>
                    <div className="ex-exp-date">{exp.duration}</div>
                  </div>
                  <div className="ex-exp-company">{exp.company}</div>
                  {exp.description && <Bullets text={exp.description} className="ex-exp-desc" />}
                </div>
              ))}
            </section>
          )}

          {d.projects.length > 0 && (
            <section>
              <div className="ex-section-label">Projects</div>
              <div className="ex-proj-grid">
                {d.projects.map((pr, i) => (
                  <div key={i} className="ex-proj-card">
                    <div className="ex-proj-name">{pr.name}</div>
                    {pr.technologies && <div className="ex-proj-tech">{pr.technologies}</div>}
                    {pr.description  && <div className="ex-proj-desc">{pr.description}</div>}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// NEON  –  cyberpunk / tech developer style
// ─────────────────────────────────────────────────────────────────────────────
export const NeonTemplate = ({ profile }) => {
  const d = parse(profile);
  return (
    <div className="ne-pf pf-tpl-root">
      <div className="ne-hero">
        <div className="ne-hero-inner">
          <Avatar url={d.avatarUrl} name={d.fullName} size={96} bg="rgba(0,229,255,0.1)" color="#00e5ff" fontSize="2rem" radius="8px" />
          <div className="ne-hero-text">
            <div className="ne-name">{d.fullName || 'Your Name'}</div>
            <div className="ne-title">&gt; {d.jobTitle || 'Professional'}</div>
            <div className="ne-contacts">
              {d.email    && <span className="ne-contact">{d.email}</span>}
              {d.phone    && <span className="ne-contact">{d.phone}</span>}
              {d.location && <span className="ne-contact">{d.location}</span>}
              {d.linkedIn && <span className="ne-contact"><a href={d.linkedIn} target="_blank" rel="noreferrer">LinkedIn ↗</a></span>}
              {d.github   && <span className="ne-contact"><a href={d.github}   target="_blank" rel="noreferrer">GitHub ↗</a></span>}
            </div>
          </div>
        </div>
      </div>

      <div className="ne-body">
        {d.professionalSummary && (
          <section className="ne-section">
            <div className="ne-label">// about</div>
            <p className="ne-summary">{d.professionalSummary}</p>
          </section>
        )}

        {d.skills.length > 0 && (
          <section className="ne-section">
            <div className="ne-label">// skills</div>
            <div className="ne-skills">
              {d.skills.map((sk, i) => <span key={i} className="ne-skill">{sk}</span>)}
            </div>
          </section>
        )}

        {d.experience.length > 0 && (
          <section className="ne-section">
            <div className="ne-label">// experience</div>
            {d.experience.map((exp, i) => (
              <div key={i} className="ne-card">
                <div className="ne-card-header">
                  <div>
                    <div className="ne-card-role">{exp.position}</div>
                    <div className="ne-card-company">{exp.company}</div>
                  </div>
                  <div className="ne-card-date">{exp.duration}</div>
                </div>
                {exp.description && <Bullets text={exp.description} className="ne-card-desc" />}
              </div>
            ))}
          </section>
        )}

        <div className="ne-two-col">
          {d.education.length > 0 && (
            <section className="ne-section">
              <div className="ne-label">// education</div>
              {d.education.map((edu, i) => (
                <div key={i} className="ne-edu-item">
                  <div className="ne-edu-deg">{edu.degree}{edu.field ? ` in ${edu.field}` : ''}</div>
                  <div className="ne-edu-inst">{edu.institution}</div>
                  <div className="ne-edu-year">{edu.year}</div>
                </div>
              ))}
            </section>
          )}

          {d.projects.length > 0 && (
            <section className="ne-section">
              <div className="ne-label">// projects</div>
              {d.projects.map((pr, i) => (
                <div key={i} className="ne-proj-item">
                  <div className="ne-proj-name">{pr.name}</div>
                  {pr.technologies && <div className="ne-proj-tech">{pr.technologies}</div>}
                  {pr.description  && <div className="ne-proj-desc">{pr.description}</div>}
                </div>
              ))}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ELEGANT  –  serif, warm ivory, gold accents
// ─────────────────────────────────────────────────────────────────────────────
export const ElegantTemplate = ({ profile }) => {
  const d = parse(profile);
  return (
    <div className="el-pf pf-tpl-root">
      <div className="el-header">
        <Avatar url={d.avatarUrl} name={d.fullName} size={100} bg="#d4a017" color="#fff" fontSize="2.2rem" />
        <div className="el-header-text">
          <div className="el-name">{d.fullName || 'Your Name'}</div>
          <div className="el-divider-gold" />
          <div className="el-title">{d.jobTitle || 'Professional'}</div>
          <div className="el-contacts">
            {d.email    && <span className="el-contact">{d.email}</span>}
            {d.phone    && <span className="el-contact">{d.phone}</span>}
            {d.location && <span className="el-contact">{d.location}</span>}
            {d.linkedIn && <span className="el-contact"><a href={d.linkedIn} target="_blank" rel="noreferrer">LinkedIn</a></span>}
            {d.github   && <span className="el-contact"><a href={d.github}   target="_blank" rel="noreferrer">GitHub</a></span>}
          </div>
        </div>
      </div>

      <div className="el-body">
        {d.professionalSummary && (
          <section className="el-section">
            <div className="el-section-title">Profile</div>
            <p className="el-summary">{d.professionalSummary}</p>
          </section>
        )}

        {d.experience.length > 0 && (
          <section className="el-section">
            <div className="el-section-title">Experience</div>
            {d.experience.map((exp, i) => (
              <div key={i} className="el-exp-item">
                <div className="el-exp-left">
                  <div className="el-exp-date">{exp.duration}</div>
                </div>
                <div className="el-exp-right">
                  <div className="el-exp-role">{exp.position}</div>
                  <div className="el-exp-company">{exp.company}</div>
                  {exp.description && <Bullets text={exp.description} className="el-exp-desc" />}
                </div>
              </div>
            ))}
          </section>
        )}

        <div className="el-two-col">
          {d.skills.length > 0 && (
            <section className="el-section">
              <div className="el-section-title">Skills</div>
              <div className="el-skills">
                {d.skills.map((sk, i) => <span key={i} className="el-skill">{sk}</span>)}
              </div>
            </section>
          )}

          {d.education.length > 0 && (
            <section className="el-section">
              <div className="el-section-title">Education</div>
              {d.education.map((edu, i) => (
                <div key={i} className="el-edu-item">
                  <div className="el-edu-deg">{edu.degree}{edu.field ? ` in ${edu.field}` : ''}</div>
                  <div className="el-edu-inst">{edu.institution}</div>
                  <div className="el-edu-year">{edu.year}</div>
                </div>
              ))}
            </section>
          )}
        </div>

        {d.projects.length > 0 && (
          <section className="el-section">
            <div className="el-section-title">Projects</div>
            <div className="el-proj-grid">
              {d.projects.map((pr, i) => (
                <div key={i} className="el-proj-card">
                  <div className="el-proj-name">{pr.name}</div>
                  {pr.technologies && <div className="el-proj-tech">{pr.technologies}</div>}
                  {pr.description  && <div className="el-proj-desc">{pr.description}</div>}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};
